<template>
  <div>
    <div class="grid grid-cols-1 lg:grid-cols-1 gap-8">
      <Transition name="slide-left" v-if="!isPanelOpen && !showTransactionConfirm">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <!-- Left Side -->
          <div class="space-y-6">
            <!-- Transfer To Whom Section-->
            <div>
              <h3 class="text-sm font-medium text-gray-600 mb-4 tracking-wide">TRANSFER TO WHOM?</h3>
              <div class="relative">
                <input
                    v-model="searchBeneficiary"
                    @input="debouncedSearch"
                    class="w-full px-4 py-3 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Search Beneficiary"
                    type="text"
                />
                <svg
                    class="absolute left-3 top-3.5 h-4 w-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                  <path
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                  />
                </svg>
              </div>

              <div class="mb-6">
                <BeneficiarySelector
                    v-model="selectedBeneficiary"
                    :beneficiaries="beneficiaryList"
                    @select-beneficiary="onBeneficiarySelected"
                    @add-new-beneficiary="openNewBeneficiaryForm"
                />
                <NewBeneficiaryModal
                    :isOpen="isModalOpen"
                    @close="isModalOpen = false"
                    @submitted="searchBeneficiaries"
                    type="international"/>
              </div>
            </div>

            <!-- Account to Credit Section-->
            <div>
              <h3 class="text-sm font-medium text-gray-600 mb-4 tracking-wide">ACCOUNT TO CREDIT</h3>
              <div class="space-y-4">
                <div>
                  <FloatingLabelInput v-model="iban" input-id="iban-field" label="Account number or IBAN *"/>
                  <p v-if="errors.iban" class="text-sm text-red-500 mt-1">{{ errors.iban }}</p>
                </div>

                <div>
                  <FloatingLabelInput v-model="beneficiaryBank" input-id="bank-field" label="Beneficiary's Bank *"/>
                  <p v-if="errors.beneficiaryBank" class="text-sm text-red-500 mt-1">{{ errors.beneficiaryBank }}</p>
                </div>

                <div>
                  <FloatingLabelInput v-model="swiftCode" input-id="swift-field" label="BIC / SWIFT Code *"/>
                  <p v-if="errors.swift" class="text-sm text-red-500 mt-1">{{ errors.swift }}</p>
                </div>

                <div>
              <textarea
                  v-model="additionalInfo"
                  class="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                  placeholder="Additional clearing information (Ex: Sort Code, Fedwire/ABA, etc.)"
                  rows="4"
              ></textarea>
                </div>
              </div>
            </div>

            <!-- Additional Information -->
            <div>
              <h3 class="text-sm font-medium text-gray-600 mb-4 tracking-wide">ADDITIONAL INFORMATION</h3>
              <div class="space-y-4">
                <FloatingLabelInput v-model="intermediaryBankName" input-id="iban-field"
                                    label="Intermediary Bank Name"/>
                <FloatingLabelInput v-model="intermediaryBankCode" input-id="iban-field"
                                    label="Intermediary Bank Code (BIC)"/>
              </div>
            </div>
          </div>

          <!-- Right Side -->
          <div class="space-y-6">
            <!-- Beneficiary Section-->
            <div>
              <h3 class="text-sm font-medium text-gray-600 mb-4 tracking-wide">Beneficiary</h3>
              <div class="space-y-4">
                <div>
                  <FloatingLabelInput v-model="beneficiaryName" input-id="iban-field" label="Beneficiary name *"/>
                  <p v-if="errors.beneficiaryName" class="text-sm text-red-500 mt-1">{{ errors.beneficiaryName }}</p>
                </div>

                <div>
                  <FloatingLabelInput v-model="beneficiaryAddress" input-id="iban-field" label="Beneficiary Address *"/>
                  <p v-if="errors.beneficiaryAddress" class="text-sm text-red-500 mt-1">{{
                      errors.beneficiaryAddress
                    }}</p>
                </div>

                <div>
                  <FloatingLabelInput v-model="city" input-id="swift-field" label="City *"/>
                  <p v-if="errors.city" class="text-sm text-red-500 mt-1">{{ errors.city }}</p>
                </div>

                <div>
                  <FloatingLabelInput v-model="postCode" input-id="post-code-field" label="Post Code *"/>
                  <p v-if="errors.postCode" class="text-sm text-red-500 mt-1">{{ errors.postCode }}</p>
                </div>

                <!-- Country -->
                <div class="relative w-full">
                  <input
                      id="country-search"
                      v-model="searchQuery"
                      type="text"
                      placeholder="Select Country *"
                      class="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent peer"
                      @focus="dropdownOpen = true"
                      @blur="closeDropdown"
                  />
                  <p v-if="errors.country" class="text-sm text-red-500 mt-1">{{ errors.country }}</p>

                  <ul
                      v-if="dropdownOpen && filteredCountries.length"
                      class="absolute z-10 w-full bg-white border border-gray-300 rounded-md max-h-48 overflow-y-auto mt-1 shadow-lg"
                  >
                    <li
                        v-for="country in filteredCountries"
                        :key="country"
                        @mousedown.prevent="selectCountry(country)"
                        class="px-4 py-3 hover:bg-gray-100 cursor-pointer text-sm"
                    >
                      {{ country }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- How Much Section-->
            <div>
              <h3 class="text-sm font-medium text-gray-600 mb-4 tracking-wide">HOW MUCH?</h3>
              <div class="flex items-center gap-x-2 relative">
                <FloatingLabelInput
                    v-model="transferValue"
                    class="w-full"
                    label="Value *"
                    min="100"
                    max="50000"
                    type="number"
                    @blur="validateTransferValue"
                />
                <button class="text-gray-600 bg-gray-200 rounded px-3 py-3" type="button">{{selectedAccount?.currency.toUpperCase()}}</button>
              </div>
              <p v-if="errors.transferValue" class="text-sm text-red-500 mt-1">{{ errors.transferValue }}</p>
              <!--          <p v-if="transferValueError" class="text-sm text-red-500 mt-1">{{ transferValueError }}</p>-->

              <p class="text-sm text-gray-500 mt-1">
                For transfers over 50,000 {{selectedAccount?.currency.toUpperCase()}} please contact your account manager.
              </p>

              <div class="mt-4">
                <FloatingLabelInput v-model="description" input-id="desc-field" label="Description *"/>
                <p v-if="errors.description" class="text-sm text-red-500 mt-1">{{ errors.description }}</p>
              </div>

              <div class="mt-4">
                <FloatingLabelInput v-model="paymentReason" input-id="desc-field"
                                    label="Reason for Payment (Bank Use)"/>
              </div>
            </div>

            <!-- Supporting Documents Section-->
            <div>
              <h3 class="text-sm font-medium text-gray-600 mb-2 tracking-wide">SUPPORTING TRANSFER DOCUMENTS
                (OPTIONAL)</h3>
              <p class="text-sm text-gray-500 mb-4">You can upload multiple documents one by one</p>

              <div v-if="attachedDocuments.length > 0" class="mb-4 space-y-2">
                <div v-for="(doc, index) in attachedDocuments" :key="index"
                     class="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                  <div class="flex-1 mr-2">
                    <p class="text-gray-700 text-sm font-medium">{{ doc.file.name }}</p>
                    <input v-model="doc.description" type="text"
                           class="w-full mt-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                           placeholder="Document description"/>
                  </div>
                  <button type="button" class="text-red-500 px-2 py-1 rounded hover:bg-red-50"
                          @click="removeDocument(index)">
                    <component :is="Trash" class="w-5 h-5"/>
                  </button>
                </div>
              </div>

              <Transition name="slide-down">
                <div v-if="showDocumentForm" class="flex items-center gap-2 mb-4">
                  <div class="w-full space-y-3 transition duration-900">
                    <FloatingLabelInput v-model="documentDescription" input-id="doc-field"
                                        label="Document description"/>
                    <div class="relative">
                      <FloatingLabelFileInput
                          :key="fileInputKey"
                          ref="fileInput"
                          @change="handleFileSelect"
                          accept=".pdf,.jpg,.jpeg,.png,.bmp"
                          class="mb-4"
                          label="Select File"
                      />
                    </div>
                  </div>
                  <button class="p-4 rounded-lg shadow text-red-400 hover:bg-red-50" @click="toggleDocumentForm(false)">
                    <component :is="Trash"/>
                  </button>
                </div>
              </Transition>

              <button
                  class="w-full flex items-center justify-center px-4 py-2 border border-orange-500 text-primary rounded-md hover:bg-orange-50 transition-colors"
                  @click="toggleDocumentForm(true)">
                Attach Documents
              </button>
              <p class="text-xs text-gray-400 mt-2">Allowed file formats: pdf, jpg, jpeg, png, bmp</p>
              <p v-if="errors.documentFile" class="text-sm text-red-500 mt-1">{{ errors.documentFile }}</p>
            </div>
          </div>
        </div>
      </Transition>

      <TransactionDataOther
          :key="transactionDataKey"
          v-show="isPanelOpen && !showTransactionConfirm && otpStep" v-model="isPanelOpen"
                            :transactionData="transactionData"
                            :selectedAccount="selectedAccount"
                            @otp-verified-international="onOtpVerified"/>

      <Transition name="slide-right">
        <TransactionConfirmOther v-show="showTransactionConfirm && isPanelOpen "
                                 :loading="isLoading"
                                 :transactionData="transactionDataServer"
                                 :transactionId="transactionId"
                                 :selectedAccount="selectedAccount"
                                 @update:modelValue="handleConfirmPanelClose"/>
      </Transition>

    </div>

    <!-- Buttons -->
    <div class="flex justify-center gap-x-3 mt-10" v-if="!isPanelOpen">
      <button
          class="w-full md:w-1/4 bg-primary text-white font-semibold py-2 px-6 rounded-md transition-colors text-lg tracking-wide shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isSubmitting"
          @click="submitTransfer"
      >
        <template v-if="!isSubmitting">
          TRANSFER
        </template>
        <template v-else>
          <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
          </svg>
          Processing...
        </template>
      </button>
    </div>




  </div>
</template>

<script setup>
import {computed, onMounted, ref, defineProps, defineEmits} from "vue";

const emit = defineEmits(['update:modelValue', 'otp-verified', 'update-step'])

import FloatingLabelInput from '@/components/ui/FloatingLabelInput.vue';
import FloatingLabelFileInput from '@/components/ui/FloatingLabelFileInput.vue';
import BeneficiarySelector from '@/components/transfer/BeneficiarySelector.vue';
import NewBeneficiaryModal from "@/components/transfer/NewBeneficiaryModal.vue";
import {Trash} from 'lucide-vue-next';
import api from '@/services/api';
import debounce from 'lodash/debounce';
// import TransactionDataOther from "@/components/transfer/TransactionData.vue";
// import TransactionConfirm from "@/components/transfer/TransactionConfirm.vue";
import TransactionDataOther from "@/components/transfer/TransactionDataOther.vue";
import TransactionConfirmOther from "@/components/transfer/TransactionConfirmOther.vue";

// Form fields
const searchBeneficiary = ref("");
const isModalOpen = ref(false);
const iban = ref("");
const beneficiaryBank = ref("");
const swiftCode = ref("");
const beneficiaryName = ref("");
const beneficiaryAddress = ref("");
const city = ref("");
const postCode = ref("");
// const country = ref("");
const transferValue = ref("");
const description = ref("");
const additionalInfo = ref("");
const intermediaryBankName = ref("");
const intermediaryBankCode = ref("");
const paymentReason = ref("");
const showDocumentForm = ref(false);
const documentDescription = ref("");
const attachedDocuments = ref([]);
const errors = ref({})   // خطاها
const fileInputKey = ref(0)
const transactionDataKey = ref(0);


const props = defineProps({
  selectedAccount: {
    type: Object,
    required: true
  }
})

// Country dropdown
const searchQuery = ref('')
const selectedCountry = ref('')
const dropdownOpen = ref(false)
const isLoading = ref(false)

const isPanelOpen = ref(false)
const showTransactionConfirm = ref(false)
const otpStep = ref(false); // وقتی true شود، فرم OTP نمایش داده می‌شود
const transactionData = ref({});
const activeStep = ref(1)
const transactionId = ref(null); // این ID از سرور بعد از تایید OTP میاد
const transactionDataServer = ref(null); // این ID از سرور بعد از تایید OTP میاد
const isSubmitting = ref(false)

const onOtpVerified = async (movement) => {
  otpStep.value = false;            // فرم OTP بسته شود
  showTransactionConfirm.value = true; // صفحه تایید تراکنش نمایش داده شود
  activeStep.value = 3;             // مرحله سوم فعال شود
  transactionDataServer.value = movement;
  emit('update-step', 3)
};


const nonEuCountries = [
  "Afghanistan",
  "Albania",
  "Algeria",
  "Andorra",
  "Angola",
  "Antigua and Barbuda",
  "Argentina",
  "Armenia",
  "Australia",
  "Azerbaijan",
  "Bahamas",
  "Bahrain",
  "Bangladesh",
  "Barbados",
  "Belarus",
  "Belize",
  "Benin",
  "Bhutan",
  "Bolivia",
  "Bosnia and Herzegovina",
  "Botswana",
  "Brazil",
  "Brunei",
  "Burkina Faso",
  "Burundi",
  "Cabo Verde",
  "Cambodia",
  "Cameroon",
  "Canada",
  "Central African Republic",
  "Chad",
  "Chile",
  "China",
  "Colombia",
  "Comoros",
  "Congo",
  "Costa Rica",
  "Cuba",
  "Djibouti",
  "Dominica",
  "Dominican Republic",
  "Ecuador",
  "Egypt",
  "El Salvador",
  "Equatorial Guinea",
  "Eritrea",
  "Eswatini",
  "Ethiopia",
  "Fiji",
  "Gabon",
  "Gambia",
  "Georgia",
  "Ghana",
  "Grenada",
  "Guatemala",
  "Guinea",
  "Guinea-Bissau",
  "Guyana",
  "Haiti",
  "Honduras",
  "Iceland",
  "India",
  "Indonesia",
  "Iran",
  "Iraq",
  "Israel",
  "Jamaica",
  "Japan",
  "Jordan",
  "Kazakhstan",
  "Kenya",
  "Kiribati",
  "Kuwait",
  "Kyrgyzstan",
  "Laos",
  "Lebanon",
  "Lesotho",
  "Liberia",
  "Libya",
  "Liechtenstein",
  "Madagascar",
  "Malawi",
  "Malaysia",
  "Maldives",
  "Mali",
  "Marshall Islands",
  "Mauritania",
  "Mauritius",
  "Micronesia",
  "Moldova",
  "Monaco",
  "Mongolia",
  "Montenegro",
  "Morocco",
  "Mozambique",
  "Myanmar",
  "Namibia",
  "Nauru",
  "Nepal",
  "New Zealand",
  "Nicaragua",
  "Niger",
  "Nigeria",
  "North Korea",
  "North Macedonia",
  "Norway",
  "Oman",
  "Pakistan",
  "Palau",
  "Palestine",
  "Panama",
  "Papua New Guinea",
  "Paraguay",
  "Peru",
  "Philippines",
  "Qatar",
  "Russia",
  "Rwanda",
  "Saint Kitts and Nevis",
  "Saint Lucia",
  "Saint Vincent and the Grenadines",
  "Samoa",
  "San Marino",
  "Sao Tome and Principe",
  "Saudi Arabia",
  "Senegal",
  "Serbia",
  "Seychelles",
  "Sierra Leone",
  "Singapore",
  "Solomon Islands",
  "Somalia",
  "South Africa",
  "South Korea",
  "South Sudan",
  "Sri Lanka",
  "Sudan",
  "Suriname",
  "Switzerland",
  "Syria",
  "Taiwan",
  "Tajikistan",
  "Tanzania",
  "Thailand",
  "Timor-Leste",
  "Togo",
  "Tonga",
  "Trinidad and Tobago",
  "Tunisia",
  "Turkey",
  "Turkmenistan",
  "Tuvalu",
  "Uganda",
  "Ukraine",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
  "Uruguay",
  "Uzbekistan",
  "Vanuatu",
  "Vatican City",
  "Venezuela",
  "Vietnam",
  "Yemen",
  "Zambia",
  "Zimbabwe"
];

const filteredCountries = computed(() => {
  if (!searchQuery.value) return nonEuCountries
  return nonEuCountries.filter(c =>
      c.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

const selectCountry = (country) => {
  searchQuery.value = country
  selectedCountry.value = country
  dropdownOpen.value = false
}

const closeDropdown = () => {
  setTimeout(() => {
    dropdownOpen.value = false
  }, 150)
}

const handleConfirmPanelClose = () => {
  isPanelOpen.value = false;
  showTransactionConfirm.value = false;
  otpStep.value = true;

  // ریست فیلدهای فرم
  searchBeneficiary.value = '';
  selectedBeneficiary.value = {};
  iban.value = '';
  beneficiaryBank.value = '';
  swiftCode.value = '';
  beneficiaryName.value = '';
  beneficiaryAddress.value = '';
  city.value = '';
  postCode.value = '';
  selectedCountry.value = '';
  searchQuery.value = '';
  transferValue.value = '';
  description.value = '';
  additionalInfo.value = '';
  intermediaryBankName.value = '';
  intermediaryBankCode.value = '';
  paymentReason.value = '';
  attachedDocuments.value = [];
  documentDescription.value = '';
  showDocumentForm.value = false;

  // پاک کردن transactionData و transactionId
  transactionData.value = {};
  transactionId.value = null;

  // ریست خطاها
  errors.value = {};

  activeStep.value = 1;

  fileInputKey.value += 1;
  transactionDataKey.value += 1;
  attachedDocuments.value = [];

  // اطلاع به والد (اگر نیاز باشه)
  emit('update-step', 1);
}

const toggleDocumentForm = (value) => showDocumentForm.value = value;


// Beneficiaries
const selectedBeneficiary = ref({})
const beneficiaryList = ref([])
const onBeneficiarySelected = (beneficiary) => {
  selectedBeneficiary.value = beneficiary;
  iban.value = beneficiary.iban ? beneficiary.iban : beneficiary.account_number ? beneficiary.account_number : '';
  swiftCode.value = beneficiary.swift || '';
  beneficiaryName.value = beneficiary.name || '';
  beneficiaryAddress.value = beneficiary.beneficiary_address || '';
  beneficiaryBank.value = beneficiary.beneficiary_bank_address || '';
  additionalInfo.value = beneficiary.additional_info || '';
  intermediaryBankName.value = beneficiary.intermediary_bank_name || '';
  intermediaryBankCode.value = beneficiary.intermediary_bank_bic || '';
  city.value = beneficiary.city || '';
  selectCountry(beneficiary.country)
  postCode.value = beneficiary.post_code || '';
};
const openNewBeneficiaryForm = () => isModalOpen.value = true;

// Debounced search
const searchBeneficiaries = async () => {
  try {
    const res = await api.get('/beneficiaries/search', {
      params: {q: searchBeneficiary.value, account_type: 'international'},
      headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}
    });
    beneficiaryList.value = res.data;
  } catch (e) {
    console.error(e);
  }
};
const debouncedSearch = debounce(searchBeneficiaries, 300);

// File handling
const handleFileSelect = (file) => {
  if (!file) return;
  attachedDocuments.value.push({ file, description: documentDescription.value || '' });
  documentDescription.value = '';
  showDocumentForm.value = false;

  // پاک کردن خطاهای فایل
  if (errors.value.documentFile) delete errors.value.documentFile;
  attachedDocuments.value.forEach((doc, idx) => {
    if (errors.value[`documentDescription_${idx}`]) delete errors.value[`documentDescription_${idx}`];
  });
};



const removeDocument = (index) => attachedDocuments.value.splice(index, 1);

// Validation & submit
const transferValueError = ref('')
const validateTransferValue = () => {
  const value = Number(transferValue.value)
  if (!value) {
    transferValueError.value = 'Please enter an amount'
  } else if (value < 100) {
    transferValueError.value = 'Minimum transfer amount is €100'
  } else if (value > 50000) {
    transferValueError.value = 'Maximum transfer amount is €50,000'
  } else {
    transferValueError.value = ''
  }
}

const submitTransfer = async () => {
  if (isSubmitting.value) return // جلوگیری از کلیک مجدد
  isSubmitting.value = true


  // reset errors
  errors.value = {}

  // validate numeric value (min/max check)
  validateTransferValue()
  if (transferValueError.value) {
    errors.value.transferValue = transferValueError.value
  }

  const maxTransfer = Number(props.selectedAccount.available_balance) - 25
  if (Number(transferValue.value) > maxTransfer) {
    errors.value.transferValue = `Your requested transfer amount is higher than your available balance, including the SWIFT fee. Please reduce the amount and try again.`
  }

  // validation فیلدهای اجباری
  if (!iban.value) errors.value.iban = 'IBAN is required'
  if (!beneficiaryBank.value) errors.value.beneficiaryBank = 'Beneficiary Bank is required'
  if (!swiftCode.value) errors.value.swift = 'SWIFT Code is required'
  if (!beneficiaryName.value) errors.value.beneficiaryName = 'Beneficiary Name is required'
  if (!beneficiaryAddress.value) errors.value.beneficiaryAddress = 'Beneficiary Address is required'
  if (!city.value) errors.value.city = 'City is required'
  if (!selectedCountry.value) errors.value.country = 'Country is required'
  if (!postCode.value) errors.value.postCode = 'Post Code is required'
  if (!transferValue.value) errors.value.transferValue = errors.value.transferValue || 'Value is required'
  if (!description.value) errors.value.description = 'Description is required'

  // اگر فرم فایل باز باشه ولی چیزی وارد نشده باشه
  // if (showDocumentForm.value) {
  //   if (!documentDescription.value) {
  //     errors.value.documentDescription = 'Document description is required'
  //   }
  //   if (!attachedDocuments.value.length) {
  //     errors.value.documentFile = 'Please select a file or cancel attaching'
  //   }
  // }

  // اگر فرم فایل باز باشه ولی چیزی وارد نشده باشه
  if (showDocumentForm.value) {
    // فقط وقتی فرم باز است و کاربر هنوز فایل اضافه نکرده است
    if (!attachedDocuments.value.length) {
      errors.value.documentFile = 'Please select a file or cancel attaching'
    }

    // چک توضیح فقط برای فایل‌هایی که اضافه شده‌اند
    attachedDocuments.value.forEach((doc, idx) => {
      if (!doc.description) {
        errors.value[`documentDescription_${idx}`] = 'Document description is required'
      }
    })
  }


  // اگر خطا وجود داشته باشه -> متوقف شو
  if (Object.keys(errors.value).length > 0) {
    isSubmitting.value = false
    return
  }

  // آماده سازی کل دیتا برای نمایش و تایید
  transactionData.value = {
    account: props.selectedAccount,
    iban: iban.value,
    swift: swiftCode.value,
    beneficiaryName: beneficiaryName.value,
    beneficiaryAddress: beneficiaryAddress.value,
    beneficiaryBank: beneficiaryBank.value,
    city: city.value,
    country: selectedCountry.value,
    postCode: postCode.value,
    value: transferValue.value,
    description: description.value,
    additionalInfo: additionalInfo.value,
    intermediaryBankName: intermediaryBankName.value,
    intermediaryBankCode: intermediaryBankCode.value,
    paymentReason: paymentReason.value,
    attachments: [...attachedDocuments.value] // فایل‌ها + توضیح
  }

  try {
    // ارسال درخواست OTP
    const res = await api.post('/transfer/international/send-otp', {
      account_id: props.selectedAccount?.id,
      iban: iban.value,
      swift: swiftCode.value,
      beneficiary_name: beneficiaryName.value,
      beneficiary_address: beneficiaryAddress.value,
      beneficiary_bank_address: beneficiaryBank.value,
      city: city.value,
      country: selectedCountry.value,
      post_code: postCode.value,
      value: transferValue.value,
      description: description.value,
      additional_info: additionalInfo.value,
      intermediary_bank_name: intermediaryBankName.value,
      intermediary_bank_code: intermediaryBankCode.value,
      payment_reason: paymentReason.value,
      transaction_type: 'swift_transfer',
      attachments: attachedDocuments.value.map(doc => ({
        name: doc.file.name,
        description: doc.description
      }))
    }, {
      headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}
    })

    console.log(res)

    if (res.data.success) {
      isPanelOpen.value = true
      otpStep.value = true
      showTransactionConfirm.value = false
      activeStep.value = 2
      emit('update-step', 2)
      // transactionId.value = res.data.transaction_id;
    } else {
      errors.value.general = res.data.message || 'Failed to send OTP'
    }
  } catch (err) {
    const data = err.response?.data
    if (data?.errors) {
      for (const key in data.errors) {
        const val = data.errors[key]
        errors.value[key] = Array.isArray(val) ? val[0] : val
      }
    } else if (data?.message) {
      errors.value.general = data.message
    } else {
      errors.value.general = 'Failed to send OTP. Please try again.'
    }
    console.error('Error sending OTP:', err)
  }finally {
    isSubmitting.value = false
  }
}


onMounted(() => {
  searchBeneficiaries();
});
</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
